import { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { History, Edit2, Trash2, Check, FileText, ChevronDown, ChevronUp, MapPin, X, Save, Plus } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export default function HistoryTab({ routeHistory, deleteHistoryRoute, updateHistoryDate, updateHistoryDestinations, contacts = [], onAddStopToHistory }) {
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [editingHistoryDate, setEditingHistoryDate] = useState(null);
  const [historyDateValue, setHistoryDateValue] = useState('');
  const [deleteHistoryConfirm, setDeleteHistoryConfirm] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null); // "historyId-destIndex"
  const [editingNoteValue, setEditingNoteValue] = useState('');

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };
  const getDateInputValue = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toISOString().split('T')[0];
  };

  const getContactPostcodes = useCallback((name, currentPostcode) => {
    if (!name) return [];
    // Prefer the contact that actually has this postcode (avoid picking a same-name sibling)
    const lower = name.toLowerCase();
    const matches = contacts.filter(c => c.name?.toLowerCase() === lower);
    const exact = matches.find(c => (c.postcodes || [c.postcode]).some(p => (p || '').toUpperCase() === (currentPostcode || '').toUpperCase()));
    const contact = exact || matches[0];
    return contact?.postcodes || (contact?.postcode ? [contact.postcode] : []);
  }, [contacts]);

  const formatPhone = (phone) => {
    if (!phone) return '';
    return phone.replace(/^\+44\s*/, '0');
  };

  const getContactPhone = useCallback((name, currentPostcode) => {
    if (!name) return '';
    const lower = name.toLowerCase();
    const matches = contacts.filter(c => c.name?.toLowerCase() === lower);
    const exact = matches.find(c => (c.postcodes || [c.postcode]).some(p => (p || '').toUpperCase() === (currentPostcode || '').toUpperCase()));
    const contact = exact || matches[0];
    return formatPhone(contact?.phone || '');
  }, [contacts]);

  const startEditNote = (historyId, destIndex, currentNote) => {
    setEditingNoteId(`${historyId}-${destIndex}`);
    setEditingNoteValue(currentNote || '');
  };

  const saveNote = (entry, destIndex) => {
    const updatedDests = entry.destinations.map((d, i) =>
      i === destIndex ? { ...d, note: editingNoteValue.trim() || null } : d
    );
    updateHistoryDestinations(entry.id, updatedDests);
    setEditingNoteId(null);
    setEditingNoteValue('');
    toast.success('Note updated');
  };

  const removeStop = (entry, destIndex) => {
    const updatedDests = entry.destinations.filter((_, i) => i !== destIndex);
    updateHistoryDestinations(entry.id, updatedDests);
    toast.success('Stop removed');
  };

  if (routeHistory.length === 0) {
    return (
      <div className="sidebar-scroll p-3">
        <div className="flex flex-col items-center justify-center h-full p-8 text-center">
          <History className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-bold mb-2">No history</h3>
          <p className="text-muted-foreground text-sm">Completed routes will appear here</p>
        </div>
      </div>
    );
  }

  const sorted = [...routeHistory].sort((a, b) => new Date(b.completed_at || b.completedAt || 0) - new Date(a.completed_at || a.completedAt || 0));

  return (
    <>
      <div className="sidebar-scroll p-3">
        <div className="space-y-3">
          {sorted.map((entry) => {
            const totalStops = entry.total_stops || entry.totalStops || entry.destinations?.length || 0;
            const completedStops = entry.completed_stops || entry.completedStops || entry.destinations?.filter(d => d.completed).length || 0;
            const completedAt = entry.completed_at || entry.completedAt;
            const routeData = entry.route_data || entry.routeData;
            const totalDistance = routeData?.total_distance_km || routeData?.totalDistance || entry.totalDistance || 0;
            const totalDuration = routeData?.total_duration_min || routeData?.totalDuration || entry.totalDuration || 0;
            const isExpanded = expandedHistoryId === entry.id;
            const isEditingDate = editingHistoryDate === entry.id;

            return (
              <div key={entry.id} className="border border-border bg-card rounded-lg overflow-hidden" data-testid={`history-${entry.id}`}>
                <div className="p-4 cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => setExpandedHistoryId(isExpanded ? null : entry.id)}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {isEditingDate ? (
                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <input type="date" className="px-2 py-1 border border-border rounded text-sm bg-background" value={historyDateValue} onChange={(e) => setHistoryDateValue(e.target.value)} autoFocus />
                          <Button size="sm" onClick={() => { updateHistoryDate(entry.id, new Date(historyDateValue).toISOString()); setEditingHistoryDate(null); }}>Save</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditingHistoryDate(null)}>Cancel</Button>
                        </div>
                      ) : (
                        <>
                          <div className="font-bold">{formatDate(completedAt)}</div>
                          <div className="text-sm text-muted-foreground">{totalStops} stops &bull; {completedStops} completed &bull; {totalDistance.toFixed?.(1) || totalDistance} km</div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {!isEditingDate && (
                        <>
                          <Button variant="ghost" size="sm" className="p-2" onClick={(e) => { e.stopPropagation(); setEditingHistoryDate(entry.id); setHistoryDateValue(getDateInputValue(completedAt)); }} title="Change date"><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="p-2 hover:text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteHistoryConfirm(entry.id); }} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                        </>
                      )}
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-muted-foreground" /> : <ChevronDown className="w-5 h-5 text-muted-foreground" />}
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-border pt-3 space-y-3">
                    <div className="space-y-2">
                      {entry.destinations?.map((dest, idx) => {
                        const allPostcodes = getContactPostcodes(dest.name, dest.postcode);
                        const phone = getContactPhone(dest.name, dest.postcode);
                        const noteKey = `${entry.id}-${idx}`;
                        const isEditingThisNote = editingNoteId === noteKey;

                        return (
                          <div key={idx} className="flex items-start gap-2 text-sm group/stop">
                            <span className="w-6 h-6 flex items-center justify-center bg-primary/10 text-primary text-xs font-bold rounded shrink-0">{idx + 1}</span>
                            <div className="flex-1 min-w-0">
                              {/* Name with postcodes popover */}
                              <div className="flex items-center gap-2 flex-wrap">
                                {allPostcodes.length > 1 ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button className="font-semibold text-[#007aff] hover:underline cursor-pointer text-left">
                                        {dest.name || 'Unknown'}
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-2" align="start">
                                      <div className="text-xs font-medium text-muted-foreground mb-1.5">All postcodes for {dest.name}</div>
                                      <div className="space-y-1">
                                        {allPostcodes.map((pc, pcIdx) => (
                                          <div key={pcIdx} className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-muted/50">
                                            <MapPin className="w-3 h-3 text-[#007aff] shrink-0" />
                                            <span className="font-mono text-xs font-medium">{pc}</span>
                                            {pc.toUpperCase() === dest.postcode?.toUpperCase() && (
                                              <span className="text-[10px] bg-[#007aff]/10 text-[#007aff] px-1.5 py-0.5 rounded-full font-medium">This stop</span>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                      {phone && (
                                        <div className="mt-2 pt-2 border-t border-border text-xs text-muted-foreground flex items-center gap-1">
                                          Phone: {phone}
                                        </div>
                                      )}
                                    </PopoverContent>
                                  </Popover>
                                ) : (
                                  <span className="font-semibold text-foreground">{dest.name || 'Unknown'}</span>
                                )}
                                <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{dest.postcode}</span>
                                {dest.completed && <Check className="w-4 h-4 text-green-500" />}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="p-1 h-auto ml-auto opacity-0 group-hover/stop:opacity-100 transition-opacity hover:text-destructive"
                                  onClick={(e) => { e.stopPropagation(); removeStop(entry, idx); }}
                                  title="Remove stop"
                                  data-testid={`remove-history-stop-${entry.id}-${idx}`}
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>

                              {/* Phone */}
                              {phone && <div className="text-xs text-muted-foreground mt-0.5">Phone: {phone}</div>}

                              {/* Note - editable */}
                              {isEditingThisNote ? (
                                <div className="flex items-center gap-1.5 mt-1.5" onClick={(e) => e.stopPropagation()}>
                                  <Input
                                    value={editingNoteValue}
                                    onChange={(e) => setEditingNoteValue(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveNote(entry, idx); if (e.key === 'Escape') setEditingNoteId(null); }}
                                    placeholder="Enter note..."
                                    className="h-8 text-xs flex-1"
                                    autoFocus
                                  />
                                  <Button size="sm" className="h-8 w-8 p-0" onClick={() => saveNote(entry, idx)}><Save className="w-3.5 h-3.5" /></Button>
                                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditingNoteId(null)}><X className="w-3.5 h-3.5" /></Button>
                                </div>
                              ) : (
                                <button
                                  className="text-xs mt-1 text-left w-full group"
                                  onClick={(e) => { e.stopPropagation(); startEditNote(entry.id, idx, dest.note); }}
                                >
                                  {dest.note ? (
                                    <span className="text-muted-foreground italic bg-muted/50 p-1.5 rounded block group-hover:bg-muted transition-colors">
                                      Note: {dest.note} <Edit2 className="w-3 h-3 inline ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground/50 hover:text-muted-foreground transition-colors">
                                      + Add note
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Add stop to this route */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full border-dashed text-muted-foreground hover:text-foreground"
                      onClick={(e) => { e.stopPropagation(); onAddStopToHistory && onAddStopToHistory(entry.id); }}
                      data-testid={`add-history-stop-${entry.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Stop
                    </Button>
                    <div className="flex gap-4 text-sm text-muted-foreground border-t border-border pt-3">
                      <span><strong>Distance:</strong> {totalDistance.toFixed?.(1) || totalDistance} km</span>
                      <span><strong>Duration:</strong> {Math.round(totalDuration)} min</span>
                    </div>
                    <Button variant="outline" size="sm" className="w-full border-[#ff9500] text-[#ff9500] hover:bg-[#ff9500]/10" onClick={(e) => {
                      e.stopPropagation();
                      const lines = [];
                      let hasEntries = false;
                      entry.destinations?.forEach((dest) => {
                        if (!dest.note) return;
                        const match = dest.note.match(/(\d+)\s*$/);
                        if (!match) return;
                        const num = parseInt(match[1], 10);
                        if (num < 50) return;
                        const matches = contacts.filter(c => c.name?.toLowerCase() === dest.name?.toLowerCase());
                        const exact = matches.find(c => (c.postcodes || [c.postcode]).some(p => (p || '').toUpperCase() === (dest.postcode || '').toUpperCase()));
                        const contact = exact || matches[0];
                        const ph = formatPhone(contact?.phone || '');
                        if (!ph) return;
                        const tier = num >= 200 ? '2' : '1';
                        lines.push(`${ph} ${tier}`);
                        hasEntries = true;
                      });
                      if (!hasEntries) { toast.error('No stops with values 50+ and phone numbers'); return; }
                      const text = lines.join('\n');
                      const blob = new Blob([text], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `report-${(completedAt || '').split('T')[0] || 'export'}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success('Report exported!');
                    }} data-testid="export-report-btn"><FileText className="w-4 h-4 mr-2" /> Export Report</Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <AlertDialog open={!!deleteHistoryConfirm} onOpenChange={(open) => !open && setDeleteHistoryConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Route?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove this route from history.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { deleteHistoryRoute(deleteHistoryConfirm); setDeleteHistoryConfirm(null); }} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
